import { customElement, property } from "lit/decorators.js";
import { html, TemplateResult } from "lit";
import { MachineAnnotation } from "@/state/MachineAnnotations";
import { UserAnnotation, userAnnotationState } from "@/state/UserAnnotations";
import { Annotation, compareAnnotationOrders } from "@/state/Annotations";
import "@/components/annotations/annotation_marker";
import "@/components/annotations/annotation_tooltip";
import "@/components/annotations/selection_layer";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { DodonaElement } from "@/components/meta/dodona_element";

declare type range = {
    start: number;
    length: number;
    annotations: Annotation[];
};

const NBSP = "\u00a0";

function numberArrayEquals(a: number[], b: number[]): boolean {
    return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * renders the code with formatting and tooltips split into four layers:
 *  - the background layer renders the markup for annotations on the code (background color and underline)
 *  - the selection layer renders the background color for the selection (split off because keeping selection logic separate from existing annotations makes it much faster)
 *  - the tooltip layer handles all pointer events (text selection and tooltips)
 *  - the text layer renders the code with syntax highlighting
 *
 * @prop {number} row - The row of the code to render
 * @prop {string} renderedCode - The syntax highlighted code to render
 * @prop {string} code - The raw source code of the line.
 * @prop {MachineAnnotation[]} machineAnnotationsToMark - Machine annotations to mark on this line.
 *
 * @element d-code-listing
 */
@customElement("d-line-of-code")
export class LineOfCode extends DodonaElement {
    @property({ type: Number })
    row: number;

    @property({ type: String, attribute: "rendered-code" })
    renderedCode: string;

    @property({ type: String })
    code = "";

    @property({ attribute: false })
    machineAnnotationsToMark: MachineAnnotation[] = [];

    get codeLength(): number {
        return this.code.length;
    }

    get userAnnotationsToMark(): UserAnnotation[] {
        // Filter out undefined values in case state isn't fully populated.
        return (
            userAnnotationState.rootIdsByMarkedLine
                .get(this.row)
                ?.map((i) => userAnnotationState.byId.get(i))
                .filter(Boolean) || []
        );
    }

    get fullLineAnnotations(): UserAnnotation[] {
        return this.userAnnotationsToMark.filter((a) => !a.column && !a.columns).sort(compareAnnotationOrders);
    }

    hasValidColumn(annotation: Annotation): boolean {
        return (
            annotation.column !== undefined &&
            annotation.column !== null &&
            annotation.column >= 0 &&
            annotation.column < this.codeLength
        );
    }

    /**
     * Calculates the range of the code that is covered by the given annotation.
     * If the annotation spans multiple lines, the range will be the whole line unless this is the first or last line.
     * In that case, the range will be the part of the line that is covered by the annotation.
     * @param annotation The annotation to calculate the range for.
     */
    getRangeFromAnnotation(annotation: Annotation, index: number): { start: number; length: number; index: number } {
        const isMachineAnnotation = ["error", "warning", "info"].includes(annotation.type);

        const rowsLength = annotation.rows ?? 1;
        let lastRow = annotation.row ? annotation.row + rowsLength : 0;
        let firstRow = annotation.row ? annotation.row + 1 : 0;

        if (!isMachineAnnotation) {
            // rows on user annotations are 1-based, so we need to subtract 1
            firstRow -= 1;
            lastRow -= 1;
        }

        let start = 0;
        if (this.row === firstRow) {
            // In rare cases, machine annotations start past the end of the line, resulting in errors if we don't constrain them to be in the line
            start = this.hasValidColumn(annotation) ? annotation.column : 0;
        }

        let length = Math.max(0, this.codeLength - start);
        if (this.row === lastRow) {
            if (this.hasValidColumn(annotation)) {
                const defaultLength = isMachineAnnotation ? 0 : Math.max(0, this.codeLength - start);
                length = annotation.columns || defaultLength;
            }
        }

        return { start, length, index };
    }

    mergeRanges(
        ranges: { start: number; length: number; index: number }[],
    ): { start: number; length: number; indexes: number[] }[] {
        if (this.codeLength === 0) {
            // Still allow zero-length markers at position 0 (e.g. machine annotations at EOL).
            const zero = ranges.filter((r) => r.length === 0 && r.start === 0).map((r) => r.index);
            return zero.length ? [{ start: 0, length: 0, indexes: zero }] : [];
        }

        const annotationsByPosition: number[][] = Array.from({ length: this.codeLength }, () => []);
        for (const range of ranges) {
            for (let i = range.start; i < range.start + range.length && i < this.codeLength; i++) {
                annotationsByPosition[i].push(range.index);
            }
        }

        const zeroLengthRanges = ranges.filter((range) => range.length === 0);
        const zeroLengthIndexesByPosition: number[][] = Array.from({ length: this.codeLength + 1 }, () => []);
        for (const range of zeroLengthRanges) {
            // clamp start: can legally be codeLength for EOL markers
            const pos = Math.min(Math.max(range.start, 0), this.codeLength);
            zeroLengthIndexesByPosition[pos].push(range.index);
        }

        const rangesToReturn: { start: number; length: number; indexes: number[] }[] = [];
        let i = 0;
        while (i < this.codeLength) {
            if (zeroLengthIndexesByPosition[i].length) {
                rangesToReturn.push({ start: i, length: 0, indexes: zeroLengthIndexesByPosition[i] });
            }

            let j = 1;
            while (
                i + j < this.codeLength &&
                numberArrayEquals(annotationsByPosition[i + j], annotationsByPosition[i]) &&
                !zeroLengthIndexesByPosition[i + j].length
            ) {
                j++;
            }

            rangesToReturn.push({ start: i, length: j, indexes: annotationsByPosition[i] });
            i += j;
        }

        // Add the zero length ranges at the end of the line
        if (zeroLengthIndexesByPosition[this.codeLength].length) {
            rangesToReturn.push({
                start: this.codeLength,
                length: 0,
                indexes: zeroLengthIndexesByPosition[this.codeLength],
            });
        }

        return rangesToReturn;
    }

    get ranges(): range[] {
        const toMark: Annotation[] = [...this.machineAnnotationsToMark, ...this.userAnnotationsToMark];
        // We use indexes to simplify the equality check in mergeRanges
        const rawRanges = toMark.map((annotation, index) => this.getRangeFromAnnotation(annotation, index));
        const mergedRanges = this.mergeRanges(rawRanges);

        return mergedRanges.map((r) => ({
            start: r.start,
            length: r.length,
            annotations: r.indexes.map((i) => toMark[i]),
        }));
    }

    render(): TemplateResult {
        const backgroundLayer: Array<string | TemplateResult> = [];
        const tooltipLayer: Array<string | TemplateResult> = [];
        const code = this.code;

        for (const r of this.ranges) {
            const substring = code.substring(r.start, r.start + r.length);
            // replace every non-whitespace character with a non-breaking space
            const nonSearchableSubstring = substring.replace(/\S/g, NBSP);

            if (!r.annotations.length) {
                backgroundLayer.push(nonSearchableSubstring);
                tooltipLayer.push(nonSearchableSubstring);
            } else {
                backgroundLayer.push(
                    html`<d-annotation-marker .annotations=${r.annotations}
                        >${nonSearchableSubstring}</d-annotation-marker
                    >`,
                );
                tooltipLayer.push(
                    html`<d-annotation-tooltip .annotations=${r.annotations}
                        >${nonSearchableSubstring}</d-annotation-tooltip
                    >`,
                );
            }
        }

        // A11y:
        // - background/selection/tooltip layers are implementation details -> hide them from screen readers
        // - expose the real code line via aria-label on the text layer (so SR users get the actual code, not Rouge spans)
        return html`<div class="code-layers">
            ${
                this.fullLineAnnotations.length > 0
                    ? html`
                          <d-annotation-marker full-width="true" .annotations=${this.fullLineAnnotations}>
                              <pre
                                  class="code-line background-layer"
                                  aria-hidden="true"
                              ><span></span>${backgroundLayer}</pre>
                          </d-annotation-marker>
                      `
                    : html`<pre class="code-line background-layer" aria-hidden="true">${backgroundLayer}</pre>`
            }

            <d-selection-layer .row=${this.row} .code=${this.code} aria-hidden="true"></d-selection-layer>

            <pre class="code-line tooltip-layer" aria-hidden="true">${tooltipLayer}</pre>

            <pre class="code-line text-layer" aria-label=${code}>${unsafeHTML(this.renderedCode)}</pre>
        </div>`;
    }
}
